import React, { useState, useEffect } from 'react';
import { Product } from '../App';

type CartItem = { product: Product; quantity: number };

  export default function Cart({ 
    items, 
    setItems, 
    onCheckout, 
    checkingOut, 
    t,
    payMethod,
    setPayMethod,
    walletInfo,
    axiosClient  // ← 新增
  }: { 
    items: CartItem[]; 
    setItems: React.Dispatch<React.SetStateAction<CartItem[]>>; 
    onCheckout: () => void; 
    checkingOut: boolean; 
    t: (key: string) => string;
    payMethod: 'pcm' | 'pi';
    setPayMethod: (m: 'pcm' | 'pi') => void;
    walletInfo: any;
    axiosClient: any;  // ← 新增
  }) {

    // 如果父组件没传 walletInfo，自己获取
  const [localWalletInfo, setLocalWalletInfo] = useState<any>(null);
  const [walletLoading, setWalletLoading] = useState(false);

  useEffect(() => {
    // 每次进入购物车页面都重新获取钱包状态
    const fetchWallet = async () => {
      setWalletLoading(true);
      try {
        const res = await axiosClient.get('/wallet/status');
        if (res.data?.bound) {
          const balRes = await axiosClient.get('/wallet/balance');
          setLocalWalletInfo({
            bound: true,
            balances: balRes.data?.balances || []
          });
        } else {
          setLocalWalletInfo({ bound: false });
        }
      } catch (e) {
        setLocalWalletInfo({ bound: false });
      } finally {
        setWalletLoading(false);
      }
    };
    fetchWallet();
  }, []); // ← 空依赖数组，确保每次组件挂载都执行

  // 判断时用 localWalletInfo
  const activeWalletInfo = walletInfo || localWalletInfo;

  const updateQty = (id: string, delta: number) => {
    setItems(prev => prev.map(item => {
      if (item.product.id === id) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }));
  };

  const removeItem = (id: string) => {
    setItems(prev => prev.filter(item => item.product.id !== id));
  };

  const totalPi = items.reduce((sum, item) => sum + item.product.piPrice * item.quantity, 0);

  if (items.length === 0) {
    return (
      <div>
        <div className="search-bar"><div style={{color:'white',textAlign:'center',padding:'8px 0',fontWeight:600}}>{t('cart.title')}</div></div>
        <div className="cart-empty">
          <div className="cart-empty-icon">🛒</div>
          <p>{t('cart.empty')}</p>
          <p style={{fontSize:12,marginTop:8}}>{t('cart.emptyDesc')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="search-bar"><div style={{color:'white',textAlign:'center',padding:'8px 0',fontWeight:600}}>{t('cart.title')}</div></div>
      <div className="cart-page">
        {items.map(item => (
          <div className="cart-item" key={item.product.id}>
            <img src={item.product.image} alt={item.product.name} onError={(e: any) => e.target.src='data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80"><rect fill="%23f0f0f0" width="80" height="80"/><text x="40" y="45" text-anchor="middle" fill="%23ccc" font-size="12">No Image</text></svg>'} />
            <div className="cart-item-info">
              <div className="cart-item-name">{item.product.name}</div>
              <div className="cart-item-price">π{item.product.piPrice} × {item.quantity} = π{(item.product.piPrice * item.quantity).toFixed(2)}</div>
              <div className="cart-item-qty">
                <button className="qty-btn" onClick={() => item.quantity > 1 ? updateQty(item.product.id, -1) : removeItem(item.product.id)}>-</button>
                <span className="qty-num">{item.quantity}</span>
                <button className="qty-btn" onClick={() => updateQty(item.product.id, 1)}>+</button>
              </div>
            </div>
            <span className="cart-item-delete" onClick={() => removeItem(item.product.id)}>✕</span>
          </div>
        ))}
      </div>
      <div className="cart-bottom">
        <div className="cart-total">{t('cart.total')} <span className="pi-price">π{totalPi.toFixed(2)}</span></div>
        
        {/* 支付方式选择 */}
        <div style={{display:'flex', gap:'8px', marginBottom:'10px'}}>
          <div 
            onClick={() => {
              if (walletLoading || localWalletInfo === null) {
                alert('正在检查钱包状态，请稍候...');
                return;
              }
              if (localWalletInfo?.bound) {
                setPayMethod('pcm');
              } else {
                alert('请先合并账户并激活钱包');
              }
            }}
            style={{
              flex:1,
              padding:'10px',
              border:'2px solid',
              borderColor: payMethod === 'pcm' ? '#611f69' : '#ddd',
              borderRadius:'8px',
              textAlign:'center',
              cursor: localWalletInfo?.bound ? 'pointer' : 'not-allowed',
              opacity: localWalletInfo?.bound ? 1 : 0.5,
              background: payMethod === 'pcm' ? '#f3e5f5' : '#fff'
            }}
          >
            <div style={{fontSize:'13px', fontWeight:600}}>💰 PCM余额</div>
            <div style={{fontSize:'11px', color:'#666'}}>
              {localWalletInfo?.bound 
                ? `π${localWalletInfo.balances?.find((b: any) => b.currency === 'PI')?.balance?.toFixed(2) || '0.00'}`
                : (walletLoading ? '加载中...' : '未激活')
              }
            </div>
          </div>
          
          {/* Pi 支付选项 */}
          <div 
            onClick={() => setPayMethod('pi')}
            style={{
              flex:1,
              padding:'10px',
              border:'2px solid',
              borderColor: payMethod === 'pi' ? '#611f69' : '#ddd',
              borderRadius:'8px',
              textAlign:'center',
              cursor:'pointer',
              background: payMethod === 'pi' ? '#f3e5f5' : '#fff'
            }}
          >
            <div style={{fontSize:'13px', fontWeight:600}}>π Pi钱包</div>
            <div style={{fontSize:'11px', color:'#666'}}>直接支付</div>
          </div>
        </div>
        
        <button className="btn-checkout" onClick={onCheckout} disabled={checkingOut}>
          {checkingOut ? t('cart.processing') : `${payMethod === 'pcm' ? '💰 余额支付' : 'π Pi支付'} π${totalPi.toFixed(2)}`}
        </button>
      </div>
    </div>
  );
}
